<template>
    <Card :title="$t('appointments.list')" containerClass="mb-5 mt-5">
        <template #cardBody>
          <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4"
                      id="appointments_datatable">
              <thead class="mb-5">
                <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                  <th class="min-w-100px text-center">{{ $t('appointments.label.subject') }}</th> 
                  <th class="min-w-100px text-center">{{ $t('appointments.label.start_date') }} & {{ $t('appointments.label.hour') }}</th>
                  <th class="min-w-100px text-center">{{ $t('appointments.label.end_date') }} & {{ $t('appointments.label.hour') }}</th>
                  <th class="min-w-100px text-center">{{ $t('appointments.label.number_members') }} </th>
                  <th class="min-w-100px text-center">{{ $t('appointments.label.status') }}</th>
                  <th class="w-100px text-center">{{ $t('appointments.label.actions') }}</th>
                </tr>
              </thead>
          </table>
        </template>
    </Card>
</template>

<script setup>

import {computed, nextTick, onMounted, onUpdated, ref, watch} from "vue";
import {useStore} from "vuex";
import i18n from "@/plugins/i18n";
import Ls from "@/services/ls";
import {BASE_URL} from "@/services/api";
import {useRouter} from "vue-router";
import {useLocales} from "@/composables/locales";
import Card from "@/Modules/CRM/components/Card";
import {useSwal} from "@/composables/useSwal";
import abilities from "@/composables/abilities";
import {useHasAbility} from "@/composables/hasAbility";
import {Theme} from "@/composables/theming";
import {$dateFormatter} from "@/mixins/Helpers";

const { can } = useHasAbility()

const router = useRouter()
const store = useStore()
const {locale, datatableLocaleUrl} = useLocales()
const {showSwal} = useSwal()

const props = defineProps({
  filter: {
    type: Object,
    default: () => ({
        status: 'all',
        type: 'all',
        start_date: '',
        end_date: '',
        search: '',
       project:  null

    })
  }
})

const table = ref()
const query = ref(props.filter)

const status = computed(() => store.getters['appointments/appointmentStatus'])

watch(() => locale.value, (value) => {
  nextTick(() => fetchDatas() )
}, { deep: true })

watch(() => query.value.status, (newValue) => {
  table.value.ajax.url(`${BASE_URL}/get/appointments/datatables?status=${query.value.status == 'all' ? '' : query.value.status}&appointmentType=${query.value.type == 'all' ? '' : query.value.type}&startDate=${query.value.start_date}&endDate=${query.value.end_date}&project=${query.value.project}`).load()
})

watch(() => query.value.type, (newValue) => {
  table.value.ajax.url(`${BASE_URL}/get/appointments/datatables?status=${query.value.status == 'all' ? '' : query.value.status}&appointmentType=${query.value.type == 'all' ? '' : query.value.type}&startDate=${query.value.start_date}&endDate=${query.value.end_date}&project=${query.value.project}`).load()
})

watch(() => [query.value.start_date, query.value.end_date], (newValue) => {
  table.value.ajax.url(`${BASE_URL}/get/appointments/datatables?status=${query.value.status == 'all' ? '' : query.value.status}&appointmentType=${query.value.type == 'all' ? '' : query.value.type}&startDate=${query.value.start_date}&endDate=${query.value.end_date}&project=${query.value.project}`).load()
})

function fetchDatas() {
  const AUTH_TOKEN = Ls.get('auth.token')
  const pageLimit = Ls.get('pageLength')

  table.value = $("#appointments_datatable").DataTable({
    destroy: true,
    responsive: !0,
    searchDelay: 500,
    processing: !0,
    serverSide: !0,
    pageLength: pageLimit ||  5,
    lengthMenu: [5, 10, 25, 50, 75, 100 ],
    order: [[ 1, 'desc']],
    ajax: {
      url: BASE_URL + `/get/appointments/datatables?status=${query.value.status == 'all' ? '' : query.value.status}&appointmentType=${query.value.type == 'all' ? '' : query.value.type}&startDate=${query.value.start_date}&endDate=${query.value.end_date}&project=${query.value.project}`,
      type: "GET",
      beforeSend: function (request) {
        request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
        request.setRequestHeader("Company", Ls.get('current.company'));
      }
    },
    columns: [
      { data: "title" }, { data: "start_time" }, { data: "end_time" }, { data: "participants_count" }, { data: "status" }, { data: "Actions", responsivePriority: -1 }
    ],
    columnDefs: [
      {
        targets: -6,
        order: "desc",
        className: "dt-center text-start clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -5,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + $dateFormatter(t, locale.value, false , true) + "</span>";

        },
      },

      {
        targets: -4,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + $dateFormatter(t, locale.value, false , true) + "</span>";
        },
      },
      {
        targets: -3,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -2,
        orderable: false,
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          let status = getAppointmentStatusName(t)
          const locale = i18n.global.locale.value
          let color = status.color
          status = status[locale]
          return '<span class="badge badge-light-' + color +'" style="font-size:13px; margin:3px; padding:5px"><span style="margin-left: 5px" class="valueRow' + l.row + ' label label-success label-inline font-weight-bolder mr-2 edit_value_btn">' + status + '</span></span>'
        },
      },
      {
        width: "15%",
        targets: -1,
        "className": "dt-center text-center",
        title: "Actions",
        orderable: !1,
        render: (t, a, e, l) => {
          let render=""
          if(can(abilities.VIEW_APPOINTMENT) || can(abilities.VIEW_ALL_APPOINTMENT)){
            render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon show_btn" title="'+ i18n.global.t('button.show') +'"><i  data-id="'+ e.id +'" class="fas fa-eye fs-2" style="color: '+Theme.gray +'"></i></a>'
          }
          if((can(abilities.UPDATE_APPOINTMENT) || can(abilities.UPDATE_ALL_APPOINTMENT)) && ['SCHEDULED','IN_PROGRESS'].includes(e.status)){
            render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon edit_btn" title="'+ i18n.global.t('button.edit') +'"><i  data-id="'+ e.id +'" class="fas fa-edit fs-2"  style="color: '+Theme.gray +'"></i></a>'
          }
          if((can(abilities.DELETE_APPOINTMENT) || can(abilities.DELETE_ALL_APPOINTMENT)) && ['SCHEDULED','IN_PROGRESS'].includes(e.status)){
            render += '<a href="javascript:;" data-row="'+ l.row +'" data-id="'+ e.id +'" class="btn btn-sm btn-clean btn-icon delete_btn" title="'+ i18n.global.t('button.delete') +'"><i data-row="'+ l.row +'" data-id="'+ e.id +'" class="fas fa-trash fs-2" style="color: '+Theme.gray +'" ></i></a>'
          }
          return render
        },
      },

    ],
    language: {
      url: datatableLocaleUrl.value,
    }
  });

  $(document).on("click", "#appointments_datatable .show_btn", (e) => {
    router.push({ name: 'appointments-view', params: { id: e.target.getAttribute('data-id') } })
  });

  $(document).on("click", "#appointments_datatable .edit_btn", (e) => {
    router.push({ name: 'appointments-edit', params: { id: e.target.getAttribute('data-id') } })
  });

  $(document).on("click", "#appointments_datatable .delete_btn", (e) => {
    deleteAppointment(e.target.getAttribute('data-id'))
  });
}

function loadData() {
  Promise.all([
    fetchDatas()
  ])
}

function deleteAppointment(id){
  showSwal(
        i18n.global.t('appointments.swal.deleted'),
        i18n.global.t('appointments.swal.deleted_text')
    ).then( async (result) => {
        if (result.value) {
            store.dispatch('appointments/deleteAppointment', id)
                .then(() => {
                    window.toastr['success'](i18n.global.t('notification.appointment_deleted'))
                    table.value.ajax.reload()
                })
                .catch(() => {
                    
                })
        }
    })
}

function getAppointmentStatusName(val) {
  const {color, fr, en} = status.value.find(el => el.value === val) || ''
  return {color, fr, en}
}

onMounted( () => {
  loadData()
})


</script>